// CommandHistoryDialog.tsx
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import { Fighter, CommandHistory } from "../../../model/Dto/Fighter";

interface CommandHistoryDialogProps {
  fighter: Fighter;
  open: boolean;
  onClose: () => void;
}

export default function CommandHistoryDialog({
  fighter,
  open,
  onClose,
}: CommandHistoryDialogProps) {
  const groupedHistory = groupCommandsByTime(fighter.commandHistory);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        История команд для бойца - {fighter.name}
        <Typography variant="subtitle2" color="text.secondary">
          Всего команд: {fighter.commandHistory.length}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {fighter.commandHistory.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            Не найдено ни одной команды
          </Typography>
        ) : (
          <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {Object.entries(groupedHistory).map(([timeGroup, commands]) => (
              <Box key={timeGroup}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    px: 2,
                    py: 1,
                    backgroundColor: 'grey.100',
                    fontWeight: 'bold',
                    fontSize: '0.9rem'
                  }}
                >
                  {timeGroup}
                </Typography>
                {commands.map((command) => (
                  <CommandHistoryListItem 
                    key={command.id} 
                    command={command} 
                  />
                ))}
                <Divider />
              </Box>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Компонент для отдельной записи истории
interface CommandHistoryListItemProps {
  command: CommandHistory;
}

function CommandHistoryListItem({ command }: CommandHistoryListItemProps) {
  const formatDateTime = (date: Date) => {
    return new Date(date).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <ListItem sx={{ px: 2, py: 1 }}>
      <ListItemText
        primary={
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {command.description}
          </Typography>
        }
        secondary={
          <Typography variant="caption" color="text.secondary">
            {formatDateTime(command.createdAt)}
          </Typography>
        }
      />
    </ListItem>
  );
}

// Функция для группировки команд по времени
function groupCommandsByTime(commands: CommandHistory[]) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const groups: Record<string, CommandHistory[]> = {
    'Сегодня': [],
    'Вчера': [],
    'На этой неделе': [],
    'В этом месяце': [],
    'Ранее': []
  };

  commands.forEach(command => {
    const commandDate = new Date(command.createdAt);
    
    if (commandDate >= today) {
      groups['Сегодня'].push(command);
    } else if (commandDate >= yesterday) {
      groups['Вчера'].push(command);
    } else if (commandDate >= weekAgo) {
      groups['На этой неделе'].push(command);
    } else if (commandDate.getMonth() === now.getMonth() && commandDate.getFullYear() === now.getFullYear()) {
      groups['В этом месяце'].push(command);
    } else {
      groups['Ранее'].push(command);
    }
  });

  // Убираем пустые группы
  return Object.fromEntries(
    Object.entries(groups).filter(([_, items]) => items.length > 0)
  );
}